import { useEffect, useState } from 'react';
import { Avatar, Badge, Banner, Button, Card, Dialog, Icon, SectionTitle, Segmented } from '../../components';
import { getTheme, setTheme, type ThemePreference } from '../../lib/branding';
import { APP_VERSION, getDeviceId } from '../../lib/device';
import { useI18n } from '../../lib/i18n';
import { Link } from '../../lib/router';
import { packAge } from '../../lib/sync/pack';
import { refreshPack } from '../../app/pack-refresh';
import { rejectionKey } from '../../lib/sync/rejections';
import { PageHeader } from '../../app/AppShell';
import { useOnline, useSyncStatus } from '../../app/hooks';
import { useMe } from '../../app/me';
import { NAV } from '../../app/nav';
import { useServices } from '../../app/services';
import { signOut } from '../../app/session-actions';
import { useToast } from '../../components/Toast';
import { SetPinScreen } from '../auth/AppLock';
import { LanguagePicker } from './LanguagePicker';

function SyncPanel() {
  const { t, fmt } = useI18n();
  const services = useServices();
  const { sync, api, storage, drafts } = services;
  const s = useSyncStatus();
  const online = useOnline();
  const toast = useToast();
  const { can, me } = useMe();
  const [age, setAge] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    void packAge(storage).then(setAge);
  }, [storage, refreshing]);

  return (
    <Card id="sync" className="flex flex-col gap-3 p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-bold">{t('ui.profile.sync')}</h2>
        <Badge tone={!online ? 'warning' : s.rejected ? 'danger' : s.pending ? 'info' : 'success'}>
          {!online ? t('ui.sync.offline') : s.pending ? t('sync.pending', { count: s.pending }) : t('sync.allSynced')}
        </Badge>
      </div>
      {s.lastFlushAt && <p className="text-sm text-muted">{t('ui.profile.lastSync', { time: fmt.dateTime(new Date(s.lastFlushAt).toISOString()) })}</p>}
      {s.stale && <Banner tone="danger">{t('sync.staleWarning')}</Banner>}
      {s.rejectedItems.length > 0 && (
        <div className="flex flex-col gap-2">
          {[...new Set(s.rejectedItems.map((r) => r.entityId))].map((entityId) => {
            const r = s.rejectedItems.find((x) => x.entityId === entityId)!;
            return (
              <Banner
                key={entityId}
                tone="danger"
                title={t('ui.profile.rejected')}
                action={
                  <div className="flex flex-wrap gap-2">
                    <Link to={`/capture/${entityId}`} className="font-semibold underline">
                      {t('ui.profile.open')}
                    </Link>
                    <button type="button" className="font-semibold underline" onClick={() => sync.retry(entityId)}>
                      {t('ui.common.retry')}
                    </button>
                  </div>
                }
              >
                {t(rejectionKey(r.error))}
              </Banner>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" icon="refresh" disabled={!online} loading={s.flushing} onClick={() => sync.flush()}>
          {t('ui.profile.syncNow')}
        </Button>
        {can('survey.create') && (
          <Button
            variant="ghost"
            icon="download"
            disabled={!online}
            loading={refreshing}
            onClick={async () => {
              setRefreshing(true);
              try {
                await refreshPack({ api, storage, drafts }, me.user.id);
                toast.show(t('ui.profile.packUpdated'), 'success');
              } catch {
                toast.show(t('ui.common.loadFailed'), 'danger');
              } finally {
                setRefreshing(false);
              }
            }}
          >
            {t('ui.profile.refreshPack')}
          </Button>
        )}
      </div>
      {can('survey.create') && (
        <p className="text-sm text-muted">{age === null ? t('ui.profile.packNever') : t('ui.profile.packAge', { minutes: Math.round(age / 60_000) })}</p>
      )}
    </Card>
  );
}

export function ProfilePage() {
  const { t } = useI18n();
  const m = useMe();
  const services = useServices();
  const toast = useToast();
  const status = useSyncStatus();
  const [theme, setThemeState] = useState<ThemePreference>(getTheme);
  const [confirmOut, setConfirmOut] = useState(false);
  const [changingPin, setChangingPin] = useState(false);
  const more = NAV.filter((n) => !n.phoneTab && n.show(m));

  if (changingPin && !m.me.user.mfaExempt) return <SetPinScreen onDone={() => setChangingPin(false)} onCancel={() => setChangingPin(false)} />;

  return (
    <>
      <PageHeader title={t('ui.nav.profile')} />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          <Card className="flex items-center gap-4 p-4">
            <Avatar name={m.me.user.name || m.me.user.email} size={56} />
            <div className="min-w-0">
              <p className="truncate text-lg font-bold">{m.me.user.name}</p>
              <p className="truncate text-muted">{m.me.user.email}</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {[...new Set(m.me.grants.map((g) => g.roleId))].map((r) => (
                  <Badge key={r} tone="brand">
                    {r}
                  </Badge>
                ))}
              </div>
            </div>
          </Card>

          <SyncPanel />

          {more.length > 0 && (
            <div className="lg:hidden">
              <SectionTitle>{t('ui.profile.more')}</SectionTitle>
              <Card className="divide-y divide-border">
                {more.map((n) => (
                  <Link key={n.to} to={n.to} className="flex min-h-14 items-center gap-3 px-4">
                    <Icon name={n.icon} className="text-brand" />
                    <span className="flex-1 font-medium">{t(n.labelKey)}</span>
                    <Icon name="chevronRight" className="text-muted rtl:rotate-180" />
                  </Link>
                ))}
              </Card>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <Card className="flex flex-col gap-4 p-4">
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold">{t('ui.profile.language')}</span>
              <LanguagePicker />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold">{t('ui.profile.theme')}</span>
              <Segmented<ThemePreference>
                label={t('ui.profile.theme')}
                size="md"
                value={theme}
                onChange={(v) => {
                  if (!v) return;
                  setTheme(v);
                  setThemeState(v);
                }}
                options={[
                  { value: 'system', label: t('ui.profile.themeSystem') },
                  { value: 'light', label: t('ui.profile.themeLight') },
                  { value: 'dark', label: t('ui.profile.themeDark') },
                ]}
              />
            </div>
            {!m.me.user.mfaExempt && (
              <Button variant="secondary" icon="lock" onClick={() => setChangingPin(true)}>
                {t('ui.profile.changePin')}
              </Button>
            )}
          </Card>

          <Button variant="danger" size="lg" icon="logOut" onClick={() => setConfirmOut(true)}>
            {t('ui.profile.signOut')}
          </Button>
          <p className="text-center text-xs text-muted">
            {t('ui.profile.version', { version: APP_VERSION })} · {getDeviceId().slice(0, 8)}
          </p>
        </div>
      </div>

      <Dialog
        open={confirmOut}
        onClose={() => setConfirmOut(false)}
        title={t('ui.profile.signOutTitle')}
        tone={status.pending + status.rejected > 0 ? 'danger' : 'default'}
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirmOut(false)}>
              {t('ui.common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                setConfirmOut(false);
                const r = await signOut(services);
                if (r.keptPending) toast.show(t('ui.profile.keptPending', { count: r.keptPending }));
              }}
            >
              {t('ui.profile.signOut')}
            </Button>
          </>
        }
      >
        {status.pending + status.rejected > 0 ? t('ui.profile.signOutPending', { count: status.pending + status.rejected }) : t('ui.profile.signOutBody')}
      </Dialog>
    </>
  );
}
